import { useRef } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { NodeEditOverlayProps } from '../types/node-edit-overlay-props'

type NodeEditPropRefInput = Pick<
  NodeEditOverlayProps,
  'onPenHoverChange' | 'onContinuePath' | 'onNodeAdded' | 'onPathChange' | 'onPathCommit'
> & {
  shape: ShapeData
  geometryInteractionDisabled: boolean
}

/**
 * Stable refs so window-level effects can always call the latest callbacks without
 * being re-registered on every prop change. Each `.current` is re-synced during render.
 */
export function useNodeEditPropRefs({
  shape,
  geometryInteractionDisabled,
  onPenHoverChange,
  onContinuePath,
  onNodeAdded,
  onPathChange,
  onPathCommit,
}: NodeEditPropRefInput) {
  const onPenHoverChangeRef = useRef(onPenHoverChange)

  onPenHoverChangeRef.current = onPenHoverChange

  const geometryInteractionDisabledRef = useRef(geometryInteractionDisabled)

  geometryInteractionDisabledRef.current = geometryInteractionDisabled
  const onContinuePathRef = useRef(onContinuePath)

  onContinuePathRef.current = onContinuePath
  const onNodeAddedRef = useRef(onNodeAdded)

  onNodeAddedRef.current = onNodeAdded
  const onPathChangeRef = useRef(onPathChange)

  onPathChangeRef.current = onPathChange
  const onPathCommitRef = useRef(onPathCommit)

  onPathCommitRef.current = onPathCommit
  const shapeIdRef = useRef(shape.id)

  shapeIdRef.current = shape.id

  const shapeRotationRef = useRef(shape.rotation ?? 0)

  shapeRotationRef.current = shape.rotation ?? 0

  const shapeKindIdRef = useRef(shape.kindId)

  shapeKindIdRef.current = shape.kindId

  const hasImageInsideRef = useRef(!!shape.photoFill)

  hasImageInsideRef.current = !!shape.photoFill

  // Presence, not content (WMPCM-8969) — see the node-removal guard in `handlePenEditClick`
  // (hooks/use-pen-edit-mode.ts). Mirrors that other removal path so the pen-edit and
  // select-edit guards can't drift apart.
  const hasTextInsideRef = useRef(shape.paragraphs != null)

  hasTextInsideRef.current = shape.paragraphs != null

  return {
    onPenHoverChangeRef,
    geometryInteractionDisabledRef,
    onContinuePathRef,
    onNodeAddedRef,
    onPathChangeRef,
    onPathCommitRef,
    shapeIdRef,
    shapeRotationRef,
    shapeKindIdRef,
    hasImageInsideRef,
    hasTextInsideRef,
  }
}

export type NodeEditPropRefs = ReturnType<typeof useNodeEditPropRefs>
